import { useEffect, useMemo, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router";
import { decodeJwtPayload } from "../../redux/utils/decodeJwtPayload";
import {
  applySsoToken,
  clearAuth,
  getJwtExpiryMs,
  isSsoJwtValid,
  setSsoError,
  setSsoSession,
  useAppDispatch,
} from "../store";
import {
  SSO_ERROR_PATH,
  getPostLoginHomePath,
} from "./ssoConfig";

const DEFAULT_INVALID_TOKEN_MESSAGE =
  "Your Single Sign-On token is invalid. Please try again.";
const DEFAULT_EXPIRED_TOKEN_MESSAGE =
  "Your Single Sign-On session has expired. Please try again.";
const DEFAULT_MISSING_TOKEN_MESSAGE =
  "No Single Sign-On token was provided.";

function readQueryValue(searchParams, ...keys) {
  for (const key of keys) {
    const raw = searchParams.get(key);
    if (raw != null && String(raw).trim()) {
      return String(raw).trim();
    }
  }
  return "";
}

function describeInvalidToken(jwtToken, decodedToken) {
  if (!jwtToken || !decodedToken) {
    return DEFAULT_INVALID_TOKEN_MESSAGE;
  }
  const expiresAtMs = getJwtExpiryMs(decodedToken);
  if (expiresAtMs != null && expiresAtMs <= Date.now()) {
    return DEFAULT_EXPIRED_TOKEN_MESSAGE;
  }
  return DEFAULT_INVALID_TOKEN_MESSAGE;
}

function goToSsoError(navigate, dispatch, message) {
  dispatch(clearAuth());
  dispatch(setSsoError(message));
  navigate(`${SSO_ERROR_PATH}?errormsg=${encodeURIComponent(message)}`, {
    replace: true,
  });
}

/**
 * SSO callback page: `/ssologin.action?jwtToken=&errormsg=`
 * Persists a valid, unexpired JWT into Redux (`ssoAuth` + shared `auth`)
 * and routes to home, or sends the user to the SSO error page.
 */
export function SsoLogin() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const handledRef = useRef(false);

  const jwtToken = useMemo(
    () => readQueryValue(searchParams, "jwtToken", "JwtToken", "jwt_token"),
    [searchParams],
  );

  const errorMsg = useMemo(
    () => readQueryValue(searchParams, "errormsg", "errorMsg", "error"),
    [searchParams],
  );

  useEffect(() => {
    if (handledRef.current) {
      return;
    }
    handledRef.current = true;

    if (errorMsg) {
      goToSsoError(navigate, dispatch, errorMsg);
      return;
    }

    if (!jwtToken) {
      goToSsoError(navigate, dispatch, DEFAULT_MISSING_TOKEN_MESSAGE);
      return;
    }

    const decodedToken = decodeJwtPayload(jwtToken);
    if (!isSsoJwtValid(jwtToken, decodedToken)) {
      goToSsoError(
        navigate,
        dispatch,
        describeInvalidToken(jwtToken, decodedToken),
      );
      return;
    }

    dispatch(setSsoSession({ jwtToken, decodedToken }));
    dispatch(applySsoToken({ jwtToken, decodedToken }));
    navigate(getPostLoginHomePath(), { replace: true });
  }, [dispatch, errorMsg, jwtToken, navigate]);

  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-gray-50 px-4 dark:bg-gray-950">
      <div
        className="flex flex-col items-center gap-5"
        role="status"
        aria-live="polite"
        aria-busy="true"
      >
        <span
          className="relative inline-block h-10 w-10 animate-spin text-blue-700 dark:text-blue-400 [animation-duration:1.15s]"
          aria-hidden="true"
        >
          <span className="absolute left-1/2 top-0 block h-2.5 w-2.5 -translate-x-1/2 rounded-full bg-current" />
          <span className="absolute bottom-0 left-0 block h-2.5 w-2.5 rounded-full bg-current" />
          <span className="absolute bottom-0 right-0 block h-2.5 w-2.5 rounded-full bg-current" />
        </span>
        <p className="text-center text-base font-medium text-gray-800 dark:text-gray-100 sm:text-lg">
          Completing Single Sign-On…
        </p>
      </div>
    </div>
  );
}
